import { Injectable } from '@jack-henry/frontend-utils/di';
import { AppType, ConfigurationService } from '@treasury/core/config';
import {
    BoCompanyUserHelper,
    ConsumerCompanyUserHelper,
} from '@treasury/domain/company/user/helpers';
import { exists } from '@treasury/utils';
import {
    ProductFeatureEntitlementTypes,
    ProductFeatureLimitTypes,
} from '../product-features/types';
import {
    CompanyUserInformation,
    CompanyUserProductFeatureSummary,
    CompanyUserState,
    CompanyUserSummary,
} from './entities';
import {
    AccountAccess,
    CompanyUserIpRestrictions,
    CompanyUserTimeAccess,
    IpRestrictionAddress,
} from './entities/access';

/**
 * Company users service for BOTH the BackOffice and Consumer apps.
 */
@Injectable()
export class CompanyUserService {
    constructor(
        private readonly config: ConfigurationService,
        private readonly consumerUserHelper: ConsumerCompanyUserHelper,
        private readonly boCompanyUserHelper: BoCompanyUserHelper
    ) {}

    // Is the service being used in Backoffice app
    public readonly isBoApp = this.config.app === AppType.BackOffice;

    // Is the service being used in Consumer facing app (Channel, PWA, etc)
    public readonly isConsumerApp =
        this.config.app === AppType.Channel || this.config.app === AppType.Pwa;

    /**
     * Retrieves the product feature summary for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param companyId - The unique identifier of the company. This parameter is required when the application type is BackOffice.
     * @returns A promise that resolves to an instance of {@link CompanyUserProductFeatureSummary}
     *          containing the product feature summary for the specified user.
     * @throws Will throw an error if the request to fetch product feature summary fails or required parameter for BackOffice app is missing.
     */
    public async getCompanyUserProductFeatureSummary(
        userId: number,
        companyId?: number
    ): Promise<CompanyUserProductFeatureSummary> {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.getCompanyUserProductFeatureSummary(companyId, userId)
            : this.consumerUserHelper.getCompanyUserProductFeatureSummary(userId);
    }

    /**
     * Searches for company users.
     *
     * @param companyId - The unique identifier of the company. This parameter is required when the application type is BackOffice.
     * @returns A promise that resolves to an array of company users of {@link CompanyUserSummary}.
     * @throws Will throw an error if the request to search company users fails or the companyId parameter is missing in BackOffice app.
     */
    public async searchCompanyUsers(companyId?: number): Promise<CompanyUserSummary[]> {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? await this.boCompanyUserHelper.searchCompanyUsers(companyId)
            : await this.consumerUserHelper.searchCompanyUsers();
    }

    /**
     * Get all company users regardless of enrollment or activation status.
     *
     * @param companyId - The unique identifier of the company. This parameter is required when the application type is BackOffice.
     * @returns A promise that resolves to an array of company users of {@link CompanyUserSummary}.
     * @throws Will throw an error if the request to search company users fails or the companyId parameter is missing in BackOffice app.
     */
    public async getAllCompanyUsers(companyId?: number): Promise<CompanyUserSummary[]> {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? await this.boCompanyUserHelper.getAllCompanyUsers(companyId)
            : await this.consumerUserHelper.searchCompanyUsers({
                  activeUsersOnly: false,
                  enrolledUsersOnly: false,
                  includeRoles: true,
              });
    }

    /**
     * Retrieves User details for a specific company user.
     *
     * @param { number } userId - The unique identifier of the user within the company.
     * @param { number } companyId - The unique identifier of the company.
     * @returns A promise that resolves to an object containing a boolean of`hasPendingChanges`and {@link CompanyUserInformation}
     *          containing the user's basic information.
     * @throws Will throw an error if the request to fetch challenge details fails or the companyId is missing in BackOffice app.
     */
    public async getCompanyUserInformation(
        userId: number,
        companyId?: number
    ): Promise<{ hasPendingChanges: boolean; user: CompanyUserInformation }> {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? await this.boCompanyUserHelper.getCompanyUserInformation(companyId, userId)
            : await this.consumerUserHelper.getCompanyUserInformation(userId);
    }

    /**
     * Retrieves the state information for a specific company user.
     *
     * @param {number} userId - The unique identifier of the user within the company.
     * @param {number} companyId - The unique identifier of the company.
     * @returns {Promise<CompanyUserState>} A promise that resolves to an instance of {@link CompanyUserState}
     *          containing the user's state information.
     * @throws {Error} Will throw an error if the request to fetch user state fails.
     */
    public async getCompanyUserState(
        userId: number,
        companyId?: number
    ): Promise<CompanyUserState> {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? await this.boCompanyUserHelper.getCompanyUserState(companyId, userId)
            : await this.consumerUserHelper.getCompanyUserState(userId);
    }

    /**
     * Sets the active status of a company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param isActive - A boolean indicating whether the user should be active or not.
     * @param comments - Optional comments to include with the request.
     * @param companyId - The unique identifier of the company. This parameter is required when the application type is BackOffice.
     * @returns A promise that resolves to an instance of {@link CompanyUserSummary}
     *          containing the updated user information.
     * @throws Will throw an error if the request to set the user's active status fails or if the companyId is missing in BackOffice app.
     */
    public async setActive(
        userId: number,
        isActive: boolean,
        comments?: string,
        companyId?: number
    ): Promise<CompanyUserSummary> {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? await this.boCompanyUserHelper.setActive(companyId, userId, isActive, comments)
            : await this.consumerUserHelper.setActive(userId, isActive, comments);
    }

    /**
     * Sends an enrollment email to a specific company user.
     *
     * @param {number} userId - The unique identifier of the user within the company.
     * @param {number} companyId - The unique identifier of the company.
     * @returns A promise that resolves to the API response data after sending the enrollment email.
     * @throws Will throw an error if the request to send the enrollment email fails or the companyId is missing in BackOffice app.
     */
    public sendEnrollmentEmail(userId: number, companyId?: number) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.sendEnrollmentEmail(companyId, userId)
            : this.consumerUserHelper.sendEnrollmentEmail(userId);
    }

    /**
     * Saves a user to the recently viewed users list for a given company.
     *
     * This method updates the list of recently viewed users for a specified company by adding
     * the provided user to the beginning of the list. If the user already exists in the list,
     * it is moved to the beginning. If the list exceeds 5 users, the oldest user is removed.
     *
     * @param companyId - The ID of the company.
     * @param user - The user to be added to the recently viewed users list.
     */
    public async saveUserToRecentlyViewedUser(userId: number, companyId?: number) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.saveUserToRecentlyViewedUser(companyId, userId)
            : this.consumerUserHelper.saveUserToRecentlyViewedUser(userId);
    }

    /**
     * Retrieves the list of recently viewed users for a given company.
     *
     * @param {number} companyId - The ID of the company.
     * @returns {RecentlyViewedUsers[]} An array of recently viewed users for the specified company.
     */
    public async getRecentlyViewedCompanyUsers(companyId?: number) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.getRecentlyViewedCompanyUsers(companyId)
            : this.consumerUserHelper.getRecentlyViewedCompanyUsers();
    }

    /**
     * Discards user information that is pending submission for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param comments - Comments to include with the approval.
     * @param companyId - The unique identifier of the company.
     * @returns A promise that resolves to a boolean indicating whether the approval was successful.
     * @throws Will throw an error if the request to discard the user details fail or if the companyId is missing in BackOffice app.
     */
    public async discardCompanyUserChanges(userId: number, comments: string, companyId?: number) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.discardCompanyUserChanges(companyId, userId, comments)
            : this.consumerUserHelper.discardCompanyUserChanges(userId, comments);
    }

    /**
     * Approves a company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param comments - The comments to include with the approval.
     * @param companyId - The unique identifier of the company.
     * @returns A promise that resolves to the API response data after approving the user.
     * @throws Will throw an error if the request to approve the user fails or if the companyId is missing in BackOffice app.
     */
    public async approveCompanyUser(userId: number, comments: string, companyId?: number) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.approveCompanyUser(companyId, userId, comments)
            : this.consumerUserHelper.approveCompanyUser(userId, comments);
    }

    /**
     * Rejects a company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param comments - The comments to include with the rejection.
     * @param companyId - The unique identifier of the company.
     * @returns A promise that resolves to the API response data after rejecting the user.
     * @throws Will throw an error if the request to reject the user fails or if the companyId is missing in BackOffice app.
     */
    public async rejectCompanyUser(userId: number, comments: string, companyId?: number) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.rejectCompanyUser(companyId, userId, comments)
            : this.consumerUserHelper.rejectCompanyUser(userId, comments);
    }

    /**
     * Deletes a company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param companyId - The unique identifier of the company.
     * @returns A promise that resolves to a boolean indicating whether the user was deleted.
     * @throws Will throw an error if the request to delete the user fails or if the companyId is missing in BackOffice app.
     */
    public async deleteCompanyUser(userId: number, companyId?: number) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.deleteCompanyUser(companyId, userId)
            : this.consumerUserHelper.deleteCompanyUser(userId);
    }

    /**
     * Unlocks a company user by initiating an unlock user challenge.
     *
     * @param userId - The unique identifier of the user to be unlocked.
     * @param companyId - The unique identifier of the company.
     * @returns A promise that resolves when the restore operation is completed.
     */
    public async restoreUserAuthentication(companyId: number, userId: number) {
        this.boCompanyUserHelper.restoreUserAuthentication(companyId, userId);
    }

    /**
     * Retrieves IP Restriction details for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param companyId - The unique identifier of the company.
     * @returns A promise that resolves to an instance of `CompanyUserIpRestrictions`
     *          containing the ip restriction details for the specified user.
     * @throws Will throw an error if the request to fetch challenge details fails or if the companyId is missing in BackOffice app.
     */
    public async getCompanyUserIpRestrictions(
        companyId: number,
        userId: number
    ): Promise<CompanyUserIpRestrictions> {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.getCompanyUserIpRestrictions(companyId, userId)
            : this.consumerUserHelper.getCompanyUserIpRestrictions(userId);
    }

    /**
     * Retrieves Time Access Restriction details for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param companyId - The unique identifier of the company.
     * @returns A promise that resolves to an instance of `CompanyUserTimeAccess`
     *          containing the ip restriction details for the specified user.
     * @throws Will throw an error if the request to fetch challenge details fails or if the companyId is missing in BackOffice app.
     */
    public async getCompanyUserTimeAccessRestrictions(
        companyId: number,
        userId: number
    ): Promise<CompanyUserTimeAccess> {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.getCompanyUserTimeAccessRestrictions(companyId, userId)
            : this.consumerUserHelper.getCompanyUserTimeAccessRestrictions(userId);
    }

    /**
     *
     * @param userId - The unique identifier of the user within the company.
     * @param ipAddresses - The IP addresses to save.
     * @param restricted - Whether the IP addresses are restricted.
     * @param companyId - The unique identifier of the company. Required for BO only.
     * @returns A promise that resolves to the API response data after saving the IP restrictions.
     * @throws Will throw an error if the request to save IP restrictions fails or if the companyId is missing in BackOffice app.
     */
    public async saveCompanyUserIpRestrictions(
        userId: number,
        ipAddresses: IpRestrictionAddress[],
        restricted: boolean,
        companyId?: number
    ) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.saveCompanyUserIpRestrictions(
                  companyId,
                  userId,
                  ipAddresses,
                  restricted
              )
            : this.consumerUserHelper.saveCompanyUserIpRestrictions(
                  userId,
                  ipAddresses,
                  restricted
              );
    }

    /**
     * Saves time access restrictions for a specific company user.
     *
     * This method updates the time-based access restrictions for a user within a company.
     * It converts the time access configuration to DTO format and sends it to the API.
     *
     * @param {number} userId - The unique identifier of the user within the company.
     * @param {CompanyUserTimeAccess} timeAccess - The time access restrictions configuration to save.
     * @param {number} companyId - The unique identifier of the company.
     * @returns {Promise<any>} A promise that resolves to the API response data after saving the time access restrictions.
     * @throws {Error} Will throw an error if the request to save time access restrictions fails or if the companyId is missing in BackOffice app.
     */
    public async saveCompanyUserTimeAccessRestrictions(
        userId: number,
        timeAccess: CompanyUserTimeAccess,
        companyId?: number
    ) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.saveCompanyUserTimeAccessRestrictions(
                  companyId,
                  userId,
                  timeAccess
              )
            : this.consumerUserHelper.saveCompanyUserTimeAccessRestrictions(userId, timeAccess);
    }

    /**
     * Retrieves the account access for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param companyId - The unique identifier of the company.
     * @returns A promise that resolves to an instance of {@link CompanyUserAccountAccess}
     *          containing the account access for the specified user.
     * @throws Will throw an error if the request to fetch account access fails or if the companyId is missing in BackOffice app.
     */
    public async getCompanyUserAccountAccess(userId: number, companyId?: number) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.getCompanyUserAccountAccess(companyId, userId)
            : this.consumerUserHelper.getCompanyUserAccountAccess(userId);
    }

    /**
     * Adds access to accounts for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param accounts - The accounts to add access to.
     * @param companyId - The unique identifier of the company.
     * @returns A promise that resolves to the API response data after adding the access to the accounts or if the companyId is missing in BackOffice app.
     */
    public async addCompanyUserAccessToAccounts(
        userId: number,
        accounts: AccountAccess[],
        companyId?: number
    ) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.addCompanyUserAccessToAccounts(companyId, userId, accounts)
            : this.consumerUserHelper.addCompanyUserAccessToAccounts(userId, accounts);
    }

    /**
     * Removes access to accounts for a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @param accounts - The accounts to remove access from.
     * @returns A promise that resolves to the API response data after removing the access from the accounts.
     */
    public async removeCompanyUserAccessToAccounts(
        userId: number,
        accounts: AccountAccess[],
        companyId?: number
    ) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.removeCompanyUserAccessToAccounts(
                  companyId,
                  userId,
                  accounts
              )
            : this.consumerUserHelper.removeCompanyUserAccessToAccounts(userId, accounts);
    }

    /**
     * Unlocks a company user by initiating an unlock user challenge.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user to be unlocked.
     * @param recollect - Optional flag indicating whether to prompt the user for new 2fa credentials the next time they login.
     * @returns A promise that resolves when the unlock operation is completed.
     */
    public async unlockCompanyUser(companyId: number, userId: number, recollect: boolean = false) {
        return this.boCompanyUserHelper.unlockCompanyUser(companyId, userId, recollect);
    }

    /**
     * Unlocks a company user by initiating an unlock user challenge.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user to be unlocked.
     * @param duration - A number representing how long the user should be locked.
     * @returns A promise that resolves when the suspension operation is completed.
     */
    public async suspendUserAuthentication(companyId: number, userId: number, duration: number) {
        return this.boCompanyUserHelper.suspendUserAuthentication(companyId, userId, duration);
    }

    /**
     * Saves the information of a company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param user - The user information to save.
     * @param companyId - The unique identifier of the company.
     * @returns A promise that resolves to the API response data after saving the user information.
     * @throws Will throw an error if the request to save user information fails or if the companyId is missing in BackOffice app.
     */
    public async saveCompanyUserInformation(
        userId: number,
        user: CompanyUserInformation,
        companyId?: number
    ) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.saveCompanyUserInformation(companyId, userId, user)
            : this.consumerUserHelper.saveCompanyUserInformation(userId, user);
    }

    /**
     * Retrieves the product entitlements for a specific company user by type.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param type - The type of product feature to retrieve entitlements for.
     * @param companyId - The unique identifier of the company.
     * @returns A promise that resolves to an instance of {@link CompanyUserProductEntitlements}
     *          containing the product entitlements for the specified user.
     * @throws Will throw an error if the request to fetch product entitlements fails or if the companyId is missing in BackOffice app.
     */
    public async getCompanyUserProductEntitlementsByType(
        userId: number,
        type: ProductFeatureEntitlementTypes,
        companyId?: number
    ) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.getCompanyUserProductEntitlementsByType(
                  companyId,
                  userId,
                  type
              )
            : this.consumerUserHelper.getCompanyUserProductEntitlementsByType(userId, type);
    }

    /**
     * Retrieves the product limits for a specific company user by type.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param type - The type of product feature to retrieve limits for.
     * @param companyId - The unique identifier of the company. This parameter is required when the application type is BackOffice.
     * @returns A promise that resolves to an instance of {@link CompanyUserProductLimits}
     *          containing the product limits for the specified user.
     * @throws Will throw an error if the request to fetch product limits fails or if the companyId parameter is missing in BackOffice app.
     */
    public async getCompanyUserProductLimitsByType(
        userId: number,
        type: ProductFeatureLimitTypes,
        companyId?: number
    ) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.getCompanyUserProductLimitsByType(companyId, userId, type)
            : this.consumerUserHelper.getCompanyUserProductLimitsByType(userId, type);
    }

    /**
     * Retrieves the ACH limits for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param companyId - The unique identifier of the company.
     * @returns A promise that resolves to an instance of {@link CompanyUserAchLimits}
     *          containing the ACH limits for the specified user.
     * @throws Will throw an error if the request to fetch ACH limits fails.
     */
    public async getCompanyUserAchLimits(userId: number, companyId?: number) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.getCompanyUserAchLimits(companyId, userId)
            : this.consumerUserHelper.getCompanyUserAchLimits(userId);
    }

    /**
     * Retrieves the ACH entitlements for a specific company user.
     *
     * @param userId - The unique identifier of the user within the company.
     * @param companyId - The unique identifier of the company.
     * @returns A promise that resolves to an instance of {@link CompanyUserAchEntitlements}
     *          containing the ACH entitlements for the specified user.
     * @throws Will throw an error if the request to fetch ACH entitlements fails or if the companyId is missing in BackOffice app.
     */
    public async getCompanyUserAchEntitlements(userId: number, companyId?: number) {
        this.boCompanyIdRequired(companyId);
        return this.isBoApp && !!companyId
            ? this.boCompanyUserHelper.getCompanyUserAchEntitlements(companyId, userId)
            : this.consumerUserHelper.getCompanyUserAchEntitlements(userId);
    }

    /**
     * Retrieves the challenge details for a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an instance of `CompanyUserChallengeDetails`
     *          containing the challenge details for the specified user.
     * @throws Will throw an error if the request to fetch challenge details fails.
     */
    public async getCompanyUserChallengeDetails(companyId: number, userId: number) {
        return this.boCompanyUserHelper.getCompanyUserChallengeDetails(companyId, userId);
    }

    /**
     * Resets the authentication details for a specific user within a company.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user whose authentication details need to be reset.
     * @returns A promise that resolves to an instance of `CompanyUserChallengeDetails` containing the user's challenge details.
     * @throws An error if the operation fails or the user details cannot be retrieved.
     */
    public async resetUserAuthentication(companyId: number, userId: number) {
        return this.boCompanyUserHelper.resetUserAuthentication(companyId, userId);
    }

    private boCompanyIdRequired(companyId?: number) {
        if (this.isBoApp && !exists(companyId)) {
            throw new Error('companyId parameter is required in BackOffice app');
        }
    }
}
